import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import useMeasure from 'react-use-measure';
import { motion, useAnimate } from 'framer-motion';
import mergeRefs from 'merge-refs';

import { setCssVar } from '@/common/lib/cssVar';
import { TextXSmall } from '@/components/Typography/Typography';
import { LotteryTimer } from '@/components/ui/Timer/LotteryTimer';
import { selectCurrentInProgressLoteryData } from '@/redux/selectors/lotterySelector';
import { useAppSelector } from '@/redux/store';

import { AdditionalRewardForTickets } from '../AdditionalRewardForTickets/AdditionalRewardForTickets';
import { Title } from '../LotteryContainer/Title/Title';
import {
  LotteryRewardsAnimation,
  type LotteryRewardsAnimationsRef,
} from '../model';
import { OtherRewards } from '../OtherRewards/OtherRewards';
import { RewardValue } from '../RewardValue/RewardValue';
import { TopPlaceRewards } from '../TopPlaceRewards/TopPlaceRewards';

import css from './styles.module.scss';

const LotteryRewardContent = forwardRef<LotteryRewardsAnimationsRef>(
  (_, ref) => {
    const { t } = useTranslation();
    const [rewardRef, rewardMeasure] = useMeasure();
    const [titleRef] = useAnimate<HTMLDivElement>();
    const [topRef] = useAnimate();
    const [valueRef] = useAnimate();
    const currentLottery = useAppSelector(selectCurrentInProgressLoteryData);

    useEffect(() => {
      if (rewardMeasure && rewardMeasure.height) {
        setCssVar('--lottery-reward-top', rewardMeasure.top + 'px');
      }
    }, [rewardMeasure]);

    const commonPool = currentLottery
      ? Object.values(currentLottery.rewards).reduce(
          (sum, reward) => sum + (reward.usdt ?? 0),
          0,
        )
      : undefined;

    useImperativeHandle(
      ref,
      () => new LotteryRewardsAnimation({ titleRef, topRef, valueRef }),
      [],
    );

    return (
      <div className="w-full px-4 flex flex-col items-center">
        <motion.div ref={topRef} className="w-full flex flex-col items-center">
          {commonPool && (
            <RewardValue
              ref={mergeRefs(valueRef, rewardRef)}
              value={commonPool}
              reward="usdt"
            />
          )}
          {currentLottery?.finishedAt && (
            <LotteryTimer
              endTime={currentLottery?.finishedAt}
              className="mt-4"
            />
          )}
        </motion.div>
        <Title ref={titleRef} className="h-[56px]" />
        <TopPlaceRewards className="mt-6" />
        <OtherRewards className="mt-[2.625rem]" />
        <AdditionalRewardForTickets className="mt-8" />
        <TextXSmall className="!leading-1.2 !text-white/60 text-center mb-1 mt-5 max-w-[22rem]">
          {t(
            'You are guaranteed to receive an additional bonus based on the number of tickets you’ve collected.',
          )}
        </TextXSmall>
        <div className={css.snapPoint}></div>
      </div>
    );
  },
);

export { LotteryRewardContent };
